import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Milestone, Mail, CalendarDays, CalendarClock, FileText,
  Wand2, Users, Briefcase, CreditCard, Zap, Settings,
  BookOpen, Upload, ShieldCheck, Info, Hammer,
  LogOut, MoreHorizontal, X,
  Pin, PinOff, Search,
  FolderOpen, LifeBuoy, MessagesSquare, Library, Bug, Sparkles, UserCircle,
} from "lucide-react";
import axios from "axios";
import { NavGroup } from "@/components/NavGroup";
import { SupportDialog } from "@/components/SupportDialogs";
import useIsMobile from "@/hooks/useIsMobile";
import useHoverExpand from "@/hooks/useHoverExpand";
import useColumnWidth, { columnWidthKey } from "@/hooks/useColumnWidth";
import { collapseAllColumns } from "@/lib/columnCoordinator";
import { GlobalSearch } from "@/components/GlobalSearch";

// ── Nav configs ───────────────────────────────────────────────────────────────

const NAV_CONFIGS = {
  Lawyer: {
    main: [
      { label: "Email",     icon: Mail,            path: "/email"     },
      { label: "Calendar",  icon: CalendarDays,    path: "/calendar"  },
      { label: "Planner",   icon: Milestone,       path: "/planner"   },
      { label: "Notes",     icon: FileText,        path: "/notes"     },
      { label: "Contacts",  icon: Users,           path: "/contacts"  },
      { label: "Clients",   icon: Briefcase,       path: "/clients"   },
    ],
    bottom: [
      { label: "Billing",    icon: CreditCard, path: "/billing"    },
      { label: "Automation", icon: Zap,        path: "/automation" },
      { label: "Settings",   icon: Settings,   path: "/settings"   },
    ],
  },
  Admin: {
    main: [
      { label: "Email",     icon: Mail,            path: "/email"     },
      { label: "Calendar",  icon: CalendarDays,    path: "/calendar"  },
      { label: "Planner",   icon: Milestone,       path: "/planner"   },
      { label: "Notes",     icon: FileText,        path: "/notes"     },
      { label: "Contacts",  icon: Users,           path: "/contacts"  },
      { label: "Clients",   icon: Briefcase,       path: "/clients"   },
    ],
    bottom: [
      { label: "Billing",    icon: CreditCard, path: "/billing"    },
      { label: "Automation", icon: Zap,        path: "/automation" },
      { label: "Settings",   icon: Settings,   path: "/settings"   },
    ],
  },
  Client: {
    main: [
      { label: "Planner",     icon: Milestone,       path: "/planner"     },
      { label: "Journal",     icon: BookOpen,        path: "/journal"     },
      { label: "Upload",      icon: Upload,          path: "/upload"      },
      { label: "Disclosures", icon: ShieldCheck,     path: "/disclosures" },
      { label: "Information", icon: Info,            path: "/information" },
      { label: "Billing",     icon: CreditCard,      path: "/billing"     },
    ],
    bottom: [],
  },
};

const MY_CASE = { label: "My Case", icon: FolderOpen, children: [
  { label: "Planner",     icon: Milestone,   path: "/planner"     },
  { label: "Journal",     icon: BookOpen,    path: "/journal"     },
  { label: "Upload",      icon: Upload,      path: "/upload"      },
  { label: "Disclosures", icon: ShieldCheck, path: "/disclosures" },
] };

// DIY/Client users get a grouped (accordion) menu. "#…" paths are actions.
const diyGroups = (isPremium) => [
  { label: "DIY", icon: Hammer, path: "/DIYWizards" },
  MY_CASE,
  { label: "Resources", icon: LifeBuoy, children: [
    { label: "Forum",        icon: MessagesSquare, path: "/forum?category=compass_diy" },
    { label: "Wiki",         icon: Library,        path: "/wiki"     },
    { label: "Consult",      icon: CalendarClock,  path: "https://cal.com/kinglawnc/consult" },
  ] },
  { label: "Account", icon: UserCircle, children: [
    { label: "Information", icon: Info, path: "/information" },
    isPremium
      ? { label: "Billing", icon: CreditCard, path: "/billing" }
      : { label: "Upgrade", icon: Sparkles,   path: "/billing" },
    { label: "Report a Bug", icon: Bug, path: "#bug" },
    { label: "Sign Out", icon: LogOut, path: "#logout" },
  ] },
];

const clientGroups = () => [
  { label: "Planner", icon: Milestone, path: "/planner" },
  { label: "Evidence", icon: FolderOpen, children: [
    { label: "Disclosures", icon: ShieldCheck, path: "/disclosures" },
    { label: "Journal",     icon: BookOpen,    path: "/journal"     },
    { label: "Upload",      icon: Upload,      path: "/upload"      },
  ] },
  { label: "Account", icon: UserCircle, children: [
    { label: "Information", icon: Info,       path: "/information" },
    { label: "Billing",     icon: CreditCard, path: "/billing"     },
    { label: "Logout",      icon: LogOut,     path: "#logout"      },
  ] },
];

const flattenGroups = (groups) => groups.flatMap((g) => g.children || [g]);

const PORTAL_LOGIN_PATHS = { Lawyer: "/firm", Admin: "/firm", Client: "/client", DIY: "/DIY" };
const TAB_COUNT = 4; // number of primary tabs before "More" — Sign Out lives in More sheet

// ── Desktop NavButton ─────────────────────────────────────────────────────────

function NavButton({ item, collapsed, isActive, onClick }) {
  const Icon = item.icon;
  const testId = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className={collapsed ? "px-1 mb-0.5" : "px-2 mb-0.5"}>
      <button
        onClick={onClick}
        data-testid={testId}
        title={collapsed ? item.label : undefined}
        className={[
          "flex items-center w-full rounded-lg h-9 text-[13px] transition-all duration-150 select-none",
          collapsed ? "justify-center" : "px-3",
          isActive
            ? "bg-white font-semibold shadow-sm"
            : "text-white/80 hover:bg-white/10 hover:text-white",
        ].join(" ")}
        style={isActive ? { color: "#001C60" } : {}}
      >
        <Icon size={16} className="flex-shrink-0" />
        {!collapsed && <span className="ml-2.5 truncate">{item.label}</span>}
      </button>
    </div>
  );
}

// ── Search (Session 348) — sits right under the logo; Ctrl/Cmd+K opens it too.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

function SearchButton({ collapsed, onClick }) {
  return (
    <div className={collapsed ? "px-1 pt-2 flex-shrink-0" : "px-2 pt-2 flex-shrink-0"}>
      <button
        onClick={onClick}
        data-testid="sidebar-search-button"
        title={collapsed ? `Search (${IS_MAC ? "⌘K" : "Ctrl+K"})` : undefined}
        className={[
          "flex items-center w-full rounded-lg h-9 text-[13px] transition-colors select-none",
          collapsed
            ? "justify-center text-white/80 hover:bg-white/10 hover:text-white"
            : "px-3 bg-white/10 text-white/60 hover:bg-white/15 hover:text-white",
        ].join(" ")}
      >
        <Search size={16} className="flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="ml-2.5 truncate">Search</span>
            <kbd className="ml-auto pl-2 text-[10px] font-medium text-white/40">{IS_MAC ? "⌘K" : "Ctrl K"}</kbd>
          </>
        )}
      </button>
    </div>
  );
}

// ── Mobile "More" sheet ───────────────────────────────────────────────────────

function MoreSheet({ items, activePath, onSelect, onClose, onLogout, onSearch, title = "More", hideSearch = false }) {
  const isGroup = title !== "More";
  return (
    <div className="fixed inset-0 z-[60]" data-testid={isGroup ? `mobile-group-sheet-${title.toLowerCase().replace(/\s+/g, "-")}` : "mobile-more-sheet"}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* Sheet */}
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl
                   max-h-[70vh] flex flex-col animate-slide-up"
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2 flex-shrink-0">
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          <button
            onClick={onClose}
            data-testid="mobile-more-close"
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          <div className="grid grid-cols-4 gap-1">
            {!isGroup && !hideSearch && (
            <button
              data-testid="mobile-more-search"
              onClick={() => { onClose(); onSearch(); }}
              className="flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Search size={20} />
              <span className="text-xs font-medium leading-none">Search</span>
            </button>
            )}
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = activePath === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`mobile-more-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => { onSelect(item.path); onClose(); }}
                  className={[
                    "flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-colors",
                    isActive
                      ? "bg-[#001C60]/10 text-[#001C60]"
                      : "text-gray-600 hover:bg-gray-100",
                  ].join(" ")}
                >
                  <Icon size={20} />
                  <span className="text-xs font-medium leading-none">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Sign Out — moved here from the bottom tab bar so the main
              mobile menu stays focused on navigation. Visually separated
              and red-tinted to signal a destructive action. */}
          {!isGroup && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => { onLogout(); onClose(); }}
              data-testid="mobile-more-signout"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
                         text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
          )}
        </nav>
      </div>
    </div>
  );
}

// ── Mobile Bottom Bar ─────────────────────────────────────────────────────────

function MobileBottomBar({ config, groups, activePath, onNavigate, onLogout, onSearch, hideMore = false }) {
  const [showMore, setShowMore] = useState(false);
  const [sheetGroup, setSheetGroup] = useState(null);

  const allItems = [...config.main, ...config.bottom];
  const tabItems = groups || allItems.slice(0, TAB_COUNT);
  const moreItems = groups ? [] : allItems.slice(TAB_COUNT);
  const tabActive = (item) =>
    item.children ? item.children.some((c) => c.path === activePath) : activePath === item.path;
  const tapTab = (item) => (item.children ? setSheetGroup(item) : onNavigate(item.path));

  return (
    <>
      <nav
        data-testid="mobile-bottom-bar"
        className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch bg-white border-t border-gray-200"
        style={{ height: 56, paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {tabItems.map((item) => {
          const Icon = item.icon;
          const isActive = tabActive(item) || sheetGroup?.label === item.label;
          return (
            <button
              key={item.label}
              onClick={() => tapTab(item)}
              data-testid={`mobile-tab-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={[
                "flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors",
                isActive ? "text-[#001C60]" : "text-gray-400",
              ].join(" ")}
            >
              <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              <span className={`text-xs leading-none ${isActive ? "font-semibold" : "font-medium"}`}>
                {item.label}
              </span>
            </button>
          );
        })}

        {/* More tab */}
        {!hideMore && (groups || moreItems.length > 0) && (
          <button
            onClick={() => setShowMore(true)}
            data-testid="mobile-tab-more"
            className={[
              "flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors",
              showMore ? "text-[#001C60]" : "text-gray-400",
            ].join(" ")}
          >
            <MoreHorizontal size={20} />
            <span className="text-xs leading-none font-medium">More</span>
          </button>
        )}
      </nav>

      {showMore && (
        <MoreSheet
          items={moreItems}
          activePath={activePath}
          onSelect={onNavigate}
          onClose={() => setShowMore(false)}
          onLogout={onLogout}
          onSearch={onSearch}
          hideSearch={!!groups}
        />
      )}

      {sheetGroup && (
        <MoreSheet
          title={sheetGroup.label}
          items={sheetGroup.children}
          activePath={activePath}
          onSelect={onNavigate}
          onClose={() => setSheetGroup(null)}
          onLogout={onLogout}
          onSearch={onSearch}
        />
      )}
    </>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export default function Sidebar() {
  const isMobile = useIsMobile();
  // "Larger screen" for the always-expanded exception = wider than 799px.
  const [isLargeScreen, setIsLargeScreen] = useState(
    () => typeof window !== "undefined" && window.innerWidth > 799
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 800px)");
    const handler = (e) => setIsLargeScreen(e.matches);
    setIsLargeScreen(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [pinned, setPinned] = useState(
    () => localStorage.getItem("compass_sidebar_pinned") === "true"
  );
  const location = useLocation();
  const navigate = useNavigate();

  const userType  = localStorage.getItem("compass_user_type")  || "Client";

  // Main menu stays permanently expanded ONLY for a Client/DIY user on a
  // larger screen (both conditions required). Everyone else uses the hover
  // model with a pin toggle.
  const isClientish = userType === "Client" || userType === "DIY";
  const alwaysExpanded = isClientish && isLargeScreen;

  const hover = useHoverExpand({ enabled: !isMobile && !pinned && !alwaysExpanded, delay: 1000 });

  // Session 242 — the main menu is resizable too, and the chosen width is
  // saved on the user's account (shared hook → /api/dashboard/ui-preferences).
  const { width: menuWidth, startDrag } = useColumnWidth({
    storageKey: columnWidthKey("global", "sidebar"),
    defaultWidth: 170, minWidth: 120, maxWidth: 360,
    enabled: !isMobile,
    onDragChange: hover.setHold,
  });

  // Session 289 — the Planner is every role's home for phases, tasks and
  // meetings, so it is always in the nav (no phase gate any more).
  const isDIY = userType === "DIY";
  const [isPremium, setIsPremium] = useState(false);
  useEffect(() => {
    if (!isDIY) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("compass_token")}` } })
      .then((r) => setIsPremium(!!r.data?.is_premium)).catch(() => {});
  }, [isDIY]);
  const groups = useMemo(
    () => (isDIY ? diyGroups(isPremium) : userType === "Client" ? clientGroups() : null),
    [isDIY, isPremium, userType],
  );
  const config = useMemo(
    () => (groups ? { main: flattenGroups(groups), bottom: [] } : NAV_CONFIGS[userType] ?? NAV_CONFIGS.Client),
    [groups, userType],
  );

  const [supportKind, setSupportKind] = useState(null);
  const go = (path) => {
    if (path === "#logout") return handleLogout();
    if (/^https?:\/\//i.test(path)) return window.open(path, "_blank", "noopener,noreferrer");
    if (path.startsWith("#")) return setSupportKind(path.slice(1));
    // DIY always returns to the DIY wizard, even from another wizard on the same page.
    return navigate(path, path === "/DIYWizards" ? { state: { diyHome: Date.now() } } : undefined);
  };

  const activeGroup = groups?.find((g) => g.children?.some((c) => c.path === location.pathname))?.label || null;
  const [openGroup, setOpenGroup] = useState(activeGroup);
  useEffect(() => { if (activeGroup) setOpenGroup(activeGroup); }, [activeGroup]);

  const togglePin = () =>
    setPinned((prev) => {
      const next = !prev;
      localStorage.setItem("compass_sidebar_pinned", String(next));
      if (!next) hover.close();
      return next;
    });

  const handleLogout = () => {
    [
      "compass_token", "compass_user_type", "compass_first_name",
      "compass_last_name", "compass_picture",
    ].forEach((k) => localStorage.removeItem(k));
    navigate(PORTAL_LOGIN_PATHS[userType] ?? "/");
  };

  // Session 348 — main-menu search; Ctrl/Cmd+K opens it from anywhere.
  // Client and DIY users have no search at all.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if (userType === "Client" || userType === "DIY") return;
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const searchDialog = (
    <>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      {isDIY && <SupportDialog kind={supportKind} onClose={() => setSupportKind(null)} />}
    </>
  );

  // ── Mobile: Bottom Tab Bar ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <MobileBottomBar
          config={config}
          groups={groups}
          hideMore={isClientish}
          activePath={location.pathname}
          onNavigate={go}
          onLogout={handleLogout}
          onSearch={() => setSearchOpen(true)}
        />
        {searchDialog}
      </>
    );
  }

  // ── Desktop: shared body renderer ───────────────────────────────────────────
  const renderBody = (collapsed, { showPin = false } = {}) => (
    <>
      {/* Header */}
      {collapsed ? (
        <div
          className="flex items-center justify-center h-16 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}
          title="Hover to expand"
        >
          <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center shadow-sm">
            <img src="/compass-logo.png" alt="Compass" className="w-6 h-6" style={{ objectFit: "contain" }} />
          </div>
        </div>
      ) : (
        <div
          className="flex items-center h-16 px-3 gap-2 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}
        >
          <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <img src="/compass-logo.png" alt="Compass" className="w-6 h-6" style={{ objectFit: "contain" }} />
          </div>
          {showPin && (
            <button
              onClick={togglePin}
              data-testid="sidebar-pin-toggle"
              title={pinned ? "Unpin — auto-collapse on hover" : "Pin menu open"}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md
                         text-white/75 hover:text-white hover:bg-white/15 transition-colors ml-auto"
            >
              {pinned ? <PinOff size={18} strokeWidth={2.25} /> : <Pin size={18} strokeWidth={2.25} />}
            </button>
          )}
        </div>
      )}

      {!groups && <SearchButton collapsed={collapsed} onClick={() => setSearchOpen(true)} />}

      {/* Main Navigation — min-h-0 lets it shrink so Sign Out stays visible */}
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden min-h-0">
        {groups
          ? groups.map((g) => g.children ? (
              <NavGroup
                key={g.label}
                group={g}
                collapsed={collapsed}
                open={openGroup === g.label}
                onToggle={() => setOpenGroup((cur) => (cur === g.label ? null : g.label))}
                activePath={location.pathname}
                onSelect={go}
              />
            ) : (
              <NavButton
                key={g.label}
                item={g}
                collapsed={collapsed}
                isActive={location.pathname === g.path}
                onClick={() => go(g.path)}
              />
            ))
          : config.main.map((item) => (
          <NavButton
            key={item.path}
            item={item}
            collapsed={collapsed}
            isActive={location.pathname === item.path}
            onClick={() => navigate(item.path)}
          />
        ))}
      </nav>

      {/* Bottom pinned section */}
      {config.bottom.length > 0 && (
        <div
          className="py-2 overflow-x-hidden flex-shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}
        >
          {config.bottom.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              collapsed={collapsed}
              isActive={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            />
          ))}
        </div>
      )}

      {/* Sign Out (DIY/Client users have it under Account) */}
      {!isClientish && (
      <div className="p-2 flex-shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout-button"
          title={collapsed ? "Sign Out" : undefined}
          className={[
            "flex items-center w-full rounded-lg h-9 text-[13px]",
            "text-white/55 hover:bg-white/10 hover:text-white transition-all",
            collapsed ? "justify-center" : "px-3",
          ].join(" ")}
        >
          <LogOut size={16} className="flex-shrink-0" />
          {!collapsed && <span className="ml-2.5">Sign Out</span>}
        </button>
      </div>
      )}
    </>
  );

  const asideBase = {
    backgroundColor: "#001C60",
    overflow: "hidden",
  };

  const resizeHandle = (
    <div
      data-testid="sidebar-resize-handle"
      onMouseDown={startDrag}
      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize z-20
                 hover:bg-white/30 active:bg-white/50 transition-colors"
      title="Drag to resize"
    />
  );

  // ── Desktop: always-expanded (Client/DIY on a large screen) or pinned ───────
  if (alwaysExpanded || pinned) {
    return (
      <>
      <aside
        data-testid="sidebar"
        className="relative flex flex-col h-screen flex-shrink-0"
        style={{ width: menuWidth, ...asideBase }}
        onMouseEnter={collapseAllColumns}
      >
        {renderBody(false, { showPin: !alwaysExpanded })}
        {resizeHandle}
      </aside>
      {searchDialog}
      </>
    );
  }

  // ── Desktop: unpinned → icon rail that grows in-flow into the full menu
  //            on hover (no floating overlay — see useHoverExpand docstring).
  return (
    <div
      ref={hover.ref}
      data-testid="sidebar-rail-wrapper"
      className={[
        "relative flex-shrink-0 h-screen overflow-hidden",
        "transition-[width] duration-200 ease-out",
        hover.open ? "shadow-2xl" : "",
      ].join(" ")}
      style={{ width: hover.open ? menuWidth : 64 }}
      {...hover.handlers}
      onMouseEnter={(e) => { collapseAllColumns(); hover.handlers.onMouseEnter(e); }}
    >
      {/* The aside matches the wrapper's width so collapsed icons are centered
          inside the visible 64px rail instead of being clipped out of view at
          the 170px centre. Every row keeps a fixed height (h-16 header, h-9
          rows, py-2 groups) in BOTH states, so expanding only reveals labels —
          no item ever shifts up or down. */}
      <aside
        data-testid="sidebar"
        className="flex flex-col h-full transition-[width] duration-200 ease-out"
        style={{ width: hover.open ? menuWidth : 64, ...asideBase }}
      >
        {renderBody(!hover.open, { showPin: hover.open })}
      </aside>
      {hover.open && resizeHandle}
      {searchDialog}
    </div>
  );
}
